import React, { useState, useEffect } from 'react';
import { Form, Dropdown, Message } from 'semantic-ui-react';
import './CustomerCreate.css';
import statesAbr from '../../../states';
import { useCustomers } from '../../../providers/CustomerProvider';
import { useAuth } from '../../../providers/AuthenticationProvider';
import { useDistributors } from '../../../providers/DistributorProvider';
import { useNavigate } from 'react-router';

export const CustomerCreate = () => {
    const [companyName, setCompanyName] = useState();
    const [firstName, setFirstName] = useState();
    const [lastName, setLastName] = useState();
    const [addressOne, setAddressOne] = useState('');
    const [addressTwo, setAddressTwo] = useState('');
    const [state, setState] = useState('');
    const [city, setCity] = useState('');
    const [zipcode, setZipcode] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhoneNumber] = useState('');
    const [errors, setErrors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [distributorId, setDistributorId] = useState();
    const [distributorsList, setDistributorsList] = useState([]);
    const { createCustomer } = useCustomers();
    const { isSystemAdmin, getUserType, checkAuth } = useAuth();
    const { getDistributors } = useDistributors();
    const navigate = useNavigate();
    const WEB_PAGE = 'Customers';

    useEffect(() => {
        checkAuth(getUserType(), WEB_PAGE) && navigate('/');

        const fetchDistributors = async () => {
            const { data: distributors } = await getDistributors(null, null, 'Name', 'asc');
            setDistributorsList(distributors.map((distributor) => {
                return {
                    id: distributor.id,
                    text: distributor.name,
                    value: distributor.id
                }
            }));
        }

        fetchDistributors();
    }, [getUserType, checkAuth, getDistributors, setDistributorsList, navigate]);

    const handleSubmit = e => {
        e.preventDefault();
        if (phone.length !== 10) {
            setErrors(['Invalid phone number']);
            return;
        }
        const zipCodeValid = /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(zipcode);
        if (!zipCodeValid) {
            setErrors(['Invalid zip code']);
            return;
        }
        setLoading(true);
        createCustomer(companyName, firstName, lastName, addressOne, addressTwo, state, city, zipcode, email, phone, distributorId)
            .then(res => {
                setLoading(false);
                navigate('/customers');
            })
            .catch(err => {
                if (err?.response?.data?.errors) {
                    setErrors(
                        err.response.data.errors.map(error => {
                            return error.description;
                        })
                    );
                } else {
                    setErrors(['Failed to create a new customer.']);
                }
                setLoading(false);
            });
    };

    /** 
     * TODO verify error handling above, here is a sample response that causes the map function to fail as 'errors' isn't an array.
     * This error handling is either *completely* broken, or the API response differs based on the errors or types of errors.....not sure which
     * 
     * {
     * "type": "https://tools.ietf.org/html/rfc7231#section-6.5.1",
     * "title": "One or more validation errors occurred.",
     * "status": 400,
     * "traceId": "00-f61176c3fc220f468d5fc7eb3b9db363-524d98b4a359d548-00",
     * "errors": {
     * "$.zip": [
     * "The JSON value could not be converted to System.Nullable`1[System.Int32]. Path: $.zip | LineNumber: 0 | BytePositionInLine: 190."
     * ]
     * }
     * }
     */

    return (
        <div>
            <div className="pageHeader">
                <h1>Create a Customer</h1>
            </div>

            <Form onSubmit={handleSubmit} error={errors.length > 0} style={{ maxWidth: '800px' }}>
                <Form.Group>
                    <Form.Input
                        width={16}
                        label="Company Name"
                        onChange={e => {
                            setCompanyName(e.target.value);
                        }}
                        type="text"
                        required
                    />
                </Form.Group>
                <Form.Group widths="equal">
                    <Form.Input
                        label="Contact First Name"
                        onChange={e => {
                            setFirstName(e.target.value);
                        }}
                        type="text"
                        required
                    />
                    <Form.Input
                        label="Contact Last Name"
                        onChange={e => {
                            setLastName(e.target.value);
                        }}
                        type="text"
                        required
                    />
                </Form.Group>
                <Form.Group widths="equal">
                    <Form.Input
                        label="Primary Contact Email"
                        type={'email'}
                        onChange={e => {
                            setEmail(e.target.value);
                        }}
                        required
                    />
                    <Form.Input
                        label="Primary Contact Phone Number"
                        type="phone"
                        onChange={e => {
                            if (e.target.value == null || e.target.value === '') return;
                            let val = e.target.value.replace(/\D/g, '');
                            if (val.length > 10) {
                                val = val.substring(0, 10);
                            }
                            e.target.value = val;
                            setPhoneNumber(e.target.value);
                        }}
                        required
                    />
                </Form.Group>
                <Form.Group>
                    {
                        isSystemAdmin() ?
                            <Form.Field
                                label={'Distributor'}
                                control={Dropdown}
                                fluid
                                selection
                                search
                                options={distributorsList}
                                style={{ height: '38px' }}
                                onChange={(e, selected) => {
                                    setDistributorId(selected.value);
                                }}
                                width={16}
                            />
                            : null
                    }
                </Form.Group>
                <Form.Group>
                    <Form.Input
                        width={16}
                        label="Address Line 1"
                        onChange={e => {
                            setAddressOne(e.target.value);
                        }}
                        type="text"
                        required
                    />
                </Form.Group>
                <Form.Group>
                    <Form.Input
                        width={16}
                        label="Address Line 2"
                        onChange={e => {
                            setAddressTwo(e.target.value);
                        }}
                        type="text"
                    />
                </Form.Group>

                <Form.Group widths="equal">
                    <Form.Input
                        onChange={e => {
                            setCity(e.target.value);
                        }}
                        label="City"
                        required
                    />
                    <Form.Field
                        label={'State'}
                        control={Dropdown}
                        fluid
                        selection
                        search
                        options={statesAbr}
                        style={{ height: '38px' }}
                        onChange={(e, selected) => {
                            setState(selected.value);
                        }}
                        required
                    />
                    <Form.Input
                        onChange={e => {
                            setZipcode(e.target.value);
                        }}
                        label="Zipcode"
                        required
                    />
                </Form.Group>

                <Form.Group width={6}>
                    <Form.Button size={'large'} style={{ marginTop: '32px' }} type={'submit'} loading={loading} color={'blue'}>
                        Submit
                    </Form.Button>
                    <Form.Button
                        size={'large'}
                        style={{ marginTop: '32px' }}
                        onClick={(e) => {
                            e.preventDefault();
                            navigate('/customers');
                        }}
                    >
                        Cancel
                    </Form.Button>
                </Form.Group>
                {errors.length > 0 ? (
                    <Form.Group widths={2}>
                        <Message error list={errors} />
                    </Form.Group>
                ) : null}
            </Form>
        </div>
    );
};
